import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import {
  getWalletApiErrorMessage,
  getWalletEntityId,
  isWalletTypeRow,
  parseBankAccountsFromResponse,
  walletDataItems,
  withdrawLimitsFromResponse,
} from "../../utils/walletApiUtils";
import { getBalanceCRefAsync } from "./betSlice";

const DEFAULT_W_MIN = 100;
const DEFAULT_W_MAX = 100000;

const initialState = {
  walletTypes: [],
  withdrawMin: DEFAULT_W_MIN,
  withdrawMax: DEFAULT_W_MAX,
  tab: "BANK",
  selectedBankTypeId: "",
  selectedCryptoTypeId: "",
  selectedUpiTypeId: "",
  selectedAccountId: "",
  accounts: [],
  accountsForTypeId: "",
  isInitLoading: false,
  isAccountsLoading: false,
  savingBank: false,
  savingCrypto: false,
  savingUpi: false,
  submittingWithdraw: false,
};

export const fetchWithdrawInit = createAsyncThunk(
  "withdraw/fetchInit",
  async (_, { rejectWithValue }) => {
    try {
      const [typesRes, limitRes] = await Promise.all([
        axiosInstance.post("/wallet/bankAccountTypesGet", {
          limit: 10,
          page: 1,
          payment_type: "WITHDRAW",
        }),
        axiosInstance.post("/user/getUserShoutPeWalletLimit"),
      ]);

      const methods = walletDataItems(typesRes).filter(isWalletTypeRow);
      const bankT = methods.filter(
        (t) => String(t?.category).toUpperCase() === "BANK",
      );
      const cryptoT = methods.filter(
        (t) => String(t?.category).toUpperCase() === "CRYPTO",
      );
      const upiT = methods.filter(
        (t) => String(t?.category).toUpperCase() === "UPI",
      );

      const lim = withdrawLimitsFromResponse(limitRes);
      let withdrawMin = DEFAULT_W_MIN;
      let withdrawMax = DEFAULT_W_MAX;
      const mn = Number(lim.withdraw_min_limit);
      const mx = Number(lim.withdraw_max_limit);
      if (Number.isFinite(mn) && mn > 0) withdrawMin = mn;
      if (Number.isFinite(mx) && mx > 0) withdrawMax = mx;

      const defaultBankTypeId = bankT[0] ? getWalletEntityId(bankT[0]) : "";
      const defaultCryptoTypeId = cryptoT[0] ? getWalletEntityId(cryptoT[0]) : "";
      const defaultUpiTypeId = upiT[0] ? getWalletEntityId(upiT[0]) : "";
      const defaultTab = bankT[0]
        ? "BANK"
        : upiT[0]
          ? "UPI"
          : cryptoT[0]
            ? "CRYPTO"
            : "BANK";

      return {
        walletTypes: methods,
        withdrawMin,
        withdrawMax,
        defaultBankTypeId,
        defaultCryptoTypeId,
        defaultUpiTypeId,
        defaultTab,
      };
    } catch (error) {
      return rejectWithValue(getWalletApiErrorMessage(error));
    }
  },
);

export const fetchWithdrawAccounts = createAsyncThunk(
  "withdraw/fetchAccounts",
  async (bank_account_type_id, { rejectWithValue }) => {
    if (!bank_account_type_id) {
      return { accounts: [], bank_account_type_id: "" };
    }
    try {
      const res = await axiosInstance.post("/wallet/bankAccountGet", {
        limit: 10,
        page: 1,
        bank_account_type_id,
      });
      const accounts = parseBankAccountsFromResponse(res);
      return { accounts, bank_account_type_id };
    } catch (error) {
      return rejectWithValue(getWalletApiErrorMessage(error));
    }
  },
);

export const createWithdrawBankAccount = createAsyncThunk(
  "withdraw/createBankAccount",
  async (
    { bank_account_type, holder_name, bank_name, account_number, ifsc_code, bank_type },
    { dispatch, rejectWithValue },
  ) => {
    const body = new URLSearchParams();
    body.append("bank_account_type", bank_account_type);
    body.append("bank_account_type_category", "BANK");
    body.append("holder_name", holder_name);
    body.append("bank_name", bank_name);
    body.append("account_number", account_number);
    body.append("ifsc_code", ifsc_code);
    body.append("bank_type", bank_type);

    try {
      const res = await axiosInstance.post("/wallet/bankAccountCreate", body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (!res?.status) {
        return rejectWithValue(res?.msg || "Failed to add account");
      }
      dispatch(fetchWithdrawAccounts(bank_account_type));
      return res;
    } catch (error) {
      return rejectWithValue(getWalletApiErrorMessage(error));
    }
  },
);

export const createWithdrawCryptoAccount = createAsyncThunk(
  "withdraw/createCryptoAccount",
  async (
    { bank_account_type, holder_name, crypto_coin_type, crypto_wallet, network_id },
    { dispatch, rejectWithValue },
  ) => {
    const body = new URLSearchParams();
    body.append("bank_account_type", bank_account_type);
    body.append("bank_account_type_category", "CRYPTO");
    body.append("holder_name", holder_name);
    body.append("crypto_coin_type", crypto_coin_type);
    body.append("crypto_wallet", crypto_wallet);
    if (network_id) body.append("network_id", network_id);

    try {
      const res = await axiosInstance.post("/wallet/bankAccountCreate", body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (!res?.status) {
        return rejectWithValue(res?.msg || "Failed to add wallet");
      }
      dispatch(fetchWithdrawAccounts(bank_account_type));
      return res;
    } catch (error) {
      return rejectWithValue(getWalletApiErrorMessage(error));
    }
  },
);

export const createWithdrawUpiAccount = createAsyncThunk(
  "withdraw/createUpiAccount",
  async (
    { bank_account_type, holder_name, upi_id, upi_phone_number },
    { dispatch, rejectWithValue },
  ) => {
    const body = new URLSearchParams();
    body.append("bank_account_type", bank_account_type);
    body.append("bank_account_type_category", "UPI");
    body.append("holder_name", holder_name);
    body.append("upi_id", upi_id);
    body.append("upi_phone_number", upi_phone_number);

    try {
      const res = await axiosInstance.post("/wallet/bankAccountCreate", body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (!res?.status) {
        return rejectWithValue(res?.msg || "Failed to add UPI");
      }
      dispatch(fetchWithdrawAccounts(bank_account_type));
      return res;
    } catch (error) {
      return rejectWithValue(getWalletApiErrorMessage(error));
    }
  },
);

export const submitWithdrawRequest = createAsyncThunk(
  "withdraw/submitRequest",
  async ({ body }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/wallet/WithdrawRequestInit", body);
      if (!res?.status) {
        return rejectWithValue(res?.msg || "Withdrawal failed");
      }
      dispatch(getBalanceCRefAsync());
      return res;
    } catch (error) {
      return rejectWithValue(getWalletApiErrorMessage(error));
    }
  },
);

const withdrawSlice = createSlice({
  name: "withdraw",
  initialState,
  reducers: {
    setWithdrawTab(state, action) {
      state.tab = action.payload;
    },
    setWithdrawBankTypeId(state, action) {
      state.selectedBankTypeId = action.payload;
    },
    setWithdrawCryptoTypeId(state, action) {
      state.selectedCryptoTypeId = action.payload;
    },
    setWithdrawUpiTypeId(state, action) {
      state.selectedUpiTypeId = action.payload;
    },
    setWithdrawSelectedAccountId(state, action) {
      state.selectedAccountId = action.payload ?? "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWithdrawInit.pending, (state) => {
        state.isInitLoading = true;
      })
      .addCase(fetchWithdrawInit.fulfilled, (state, action) => {
        state.isInitLoading = false;
        state.walletTypes = action.payload.walletTypes;
        state.withdrawMin = action.payload.withdrawMin;
        state.withdrawMax = action.payload.withdrawMax;
        state.selectedBankTypeId = action.payload.defaultBankTypeId;
        state.selectedCryptoTypeId = action.payload.defaultCryptoTypeId;
        state.selectedUpiTypeId = action.payload.defaultUpiTypeId;
        state.tab = action.payload.defaultTab;
      })
      .addCase(fetchWithdrawInit.rejected, (state) => {
        state.isInitLoading = false;
      })
      .addCase(fetchWithdrawAccounts.pending, (state) => {
        state.isAccountsLoading = true;
        state.selectedAccountId = "";
      })
      .addCase(fetchWithdrawAccounts.fulfilled, (state, action) => {
        state.isAccountsLoading = false;
        state.accounts = action.payload.accounts;
        state.accountsForTypeId = action.payload.bank_account_type_id;
      })
      .addCase(fetchWithdrawAccounts.rejected, (state) => {
        state.isAccountsLoading = false;
        state.accounts = [];
        state.accountsForTypeId = "";
      })
      .addCase(createWithdrawBankAccount.pending, (state) => {
        state.savingBank = true;
      })
      .addCase(createWithdrawBankAccount.fulfilled, (state) => {
        state.savingBank = false;
      })
      .addCase(createWithdrawBankAccount.rejected, (state) => {
        state.savingBank = false;
      })
      .addCase(createWithdrawCryptoAccount.pending, (state) => {
        state.savingCrypto = true;
      })
      .addCase(createWithdrawCryptoAccount.fulfilled, (state) => {
        state.savingCrypto = false;
      })
      .addCase(createWithdrawCryptoAccount.rejected, (state) => {
        state.savingCrypto = false;
      })
      .addCase(createWithdrawUpiAccount.pending, (state) => {
        state.savingUpi = true;
      })
      .addCase(createWithdrawUpiAccount.fulfilled, (state) => {
        state.savingUpi = false;
      })
      .addCase(createWithdrawUpiAccount.rejected, (state) => {
        state.savingUpi = false;
      })
      .addCase(submitWithdrawRequest.pending, (state) => {
        state.submittingWithdraw = true;
      })
      .addCase(submitWithdrawRequest.fulfilled, (state) => {
        state.submittingWithdraw = false;
        state.selectedAccountId = "";
      })
      .addCase(submitWithdrawRequest.rejected, (state) => {
        state.submittingWithdraw = false;
      });
  },
});

export const {
  setWithdrawTab,
  setWithdrawBankTypeId,
  setWithdrawCryptoTypeId,
  setWithdrawUpiTypeId,
  setWithdrawSelectedAccountId,
} = withdrawSlice.actions;

export default withdrawSlice.reducer;

